import { useCallback, useState } from 'react';
import { getLocalStore, setLocalStore } from './common';

/**
 * Per-table view settings remembered across sessions (REG-653).
 *
 * Stored in localStorage through the shared `supa-crm` store helpers — the
 * same mechanism the grids already use for column layout — so a setting
 * survives hard refreshes, logging out, and logging back in on this browser.
 * Keys are per table (`table-zoom-<tableKey>`), so every table remembers the
 * last value the user chose for it independently.
 */

export const GRID_ZOOM_MIN = 0.7;
export const GRID_ZOOM_MAX = 1.2;
export const GRID_ZOOM_STEP = 0.1;
export const GRID_ZOOM_DEFAULT = 1;

export function clampGridZoom(value: number): number {
  if (!Number.isFinite(value)) return GRID_ZOOM_DEFAULT;
  // Snap to one decimal so stepping never accumulates float drift.
  const snapped = Math.round(value * 10) / 10;
  return Math.min(GRID_ZOOM_MAX, Math.max(GRID_ZOOM_MIN, snapped));
}

function zoomStoreKey(tableKey: string): string {
  return `table-zoom-${tableKey}`;
}

export function loadTableZoom(tableKey: string, fallback = GRID_ZOOM_DEFAULT): number {
  const stored = getLocalStore<number>(zoomStoreKey(tableKey));
  return stored == null ? fallback : clampGridZoom(stored);
}

/**
 * The table's zoom level and a setter that persists it. `tableKey` identifies
 * the logical table (e.g. `submission`, `report:funded_deals`, `rep`);
 * `fallback` is the level before the user picks one.
 */
export function useTableZoom(tableKey: string, fallback = GRID_ZOOM_DEFAULT): [number, (zoom: number) => void] {
  const [state, setState] = useState(() => ({ key: tableKey, zoom: loadTableZoom(tableKey, fallback) }));

  // Re-read storage if the same mounted component switches tables
  // (render-phase derived state, per the React docs pattern).
  if (state.key !== tableKey) {
    setState({ key: tableKey, zoom: loadTableZoom(tableKey, fallback) });
  }

  const setZoom = useCallback((next: number) => {
    const clamped = clampGridZoom(next);
    setState({ key: tableKey, zoom: clamped });
    setLocalStore(zoomStoreKey(tableKey), clamped);
  }, [tableKey]);

  return [state.zoom, setZoom];
}

/**
 * Per-list "active" filter remembered across sessions (REG-673).
 *
 * Same storage as the zoom level above, keyed `table-active-filter-<tableKey>`
 * so the rep, funder, and user lists each remember their own last choice.
 * Unknown stored values (e.g. from an older build) fall back to the default.
 */
export type ActiveFilterValue = 'active' | 'inactive' | 'all';

const ACTIVE_FILTER_VALUES: readonly ActiveFilterValue[] = ['active', 'inactive', 'all'];
export const ACTIVE_FILTER_DEFAULT: ActiveFilterValue = 'active';

function activeFilterStoreKey(tableKey: string): string {
  return `table-active-filter-${tableKey}`;
}

export function loadTableActiveFilter(tableKey: string): ActiveFilterValue {
  const stored = getLocalStore<string>(activeFilterStoreKey(tableKey));
  return ACTIVE_FILTER_VALUES.includes(stored as ActiveFilterValue)
    ? (stored as ActiveFilterValue)
    : ACTIVE_FILTER_DEFAULT;
}

export function useTableActiveFilter(tableKey: string): [ActiveFilterValue, (value: ActiveFilterValue) => void] {
  const [state, setState] = useState(() => ({ key: tableKey, value: loadTableActiveFilter(tableKey) }));

  if (state.key !== tableKey) {
    setState({ key: tableKey, value: loadTableActiveFilter(tableKey) });
  }

  const setValue = useCallback((next: ActiveFilterValue) => {
    const safe = ACTIVE_FILTER_VALUES.includes(next) ? next : ACTIVE_FILTER_DEFAULT;
    setState({ key: tableKey, value: safe });
    setLocalStore(activeFilterStoreKey(tableKey), safe);
  }, [tableKey]);

  return [state.value, setValue];
}
